import { useEffect, useRef, useState } from "react";
import useDocumentStore from "../../redux/dispatcher/useDocumentStore";
import utilsDocs from "../../shared/utils/docs";

/** ▼ API 동시 제한만 유지 */
function limit(concurrency: number) {
  let active = 0;
  const q: Array<() => void> = [];
  const next = () => {
    active--;
    q.shift()?.();
  };
  return function <T>(task: () => Promise<T>): Promise<T> {
    return new Promise<T>((resolve, reject) => {
      const run = () => {
        active++;
        task()
          .then((v) => {
            resolve(v);
            next();
          })
          .catch((e) => {
            reject(e);
            next();
          });
      };
      active < concurrency ? run() : q.push(run);
    });
  };
}
const runApi = limit(4);

export default function Thumbnail({
  syncId,
  revisionNo,
  contentName,
}: {
  syncId: string;
  revisionNo: number;
  contentName: string;
}) {
  const { documentState, getContentThumbnail, setContentThumbnail } =
    useDocumentStore();
  const [thumbUrl, setThumbUrl] = useState<string | null>(null);
  const [failed, setFailed] = useState(false);
  const mountedRef = useRef(true);

  // base64 → dataURL 변환 (이미 data:로 저장된 경우 중복 방지)
  const toDataUrl = (val: string, mime = "image/png") =>
    val.startsWith("data:") ? val : `data:${mime};base64,${val}`;

  useEffect(() => {
    mountedRef.current = true;

    const key = `${syncId}_${revisionNo}`;
    const saved = documentState.savedContentThumbnail[key];

    if (saved) {
      if (saved === "PENDING") {
        // 대기 표시 그대로
      } else if (saved === "FAIL") {
        setFailed(true);
      } else if (typeof saved === "string") {
        setThumbUrl(toDataUrl(saved));
      }
      return () => {
        mountedRef.current = false;
      };
    }

    setThumbUrl(null);
    setFailed(false);
    setContentThumbnail({ syncId, revisionNo, blob: "PENDING" });

    (async () => {
      try {
        const item: any = await runApi(() =>
          getContentThumbnail({ syncId, revisionNo })
        );
        const base64: string | undefined = item?.payload;
        if (!base64) {
          setContentThumbnail({ syncId, revisionNo, blob: "FAIL" });
          if (mountedRef.current) setFailed(true);
          return;
        }

        if (mountedRef.current) setThumbUrl(toDataUrl(base64));
        setContentThumbnail({ syncId, revisionNo, blob: base64 });
      } catch {
        setContentThumbnail({ syncId, revisionNo, blob: "FAIL" });
        if (mountedRef.current) setFailed(true);
      }
    })();

    return () => {
      mountedRef.current = false;
    };
  }, [syncId, revisionNo, contentName]);

  if (failed || !thumbUrl)
    return <>{utilsDocs.renderDocIcon(contentName, 128)}</>;

  return (
    <img
      src={thumbUrl}
      style={{ objectFit: "fill" }}
      alt="thumbnail"
      height="100%"
      loading="lazy"
      decoding="async"
    />
  );
}
